import React from 'react';

import MetaData from '../components/MetaData';
import convertToReactElements from './convertToReactElements';
import isElementFeatureEnabled from './isElementFeatureEnabled';
import replacePlaceholderValues from './replacePlaceholderValues';

/**
 * Make sure that all nested arrays are moved to the root.
 *
 * @param {*} elements
 */
const flattenElements = (elements) =>
  elements.reduce((acc, element) => {
    if (Array.isArray(element)) {
      return [...acc, ...element];
    }

    return [...acc, element];
  }, []);

/**
 * Creates an array of react componenets based on the given
 * configuration.
 *
 * If it finds a 'ScrollIntoView' element, it will wrap all
 * subsequent components into a so-called contentContainerComponent.
 * This is used to add additional styling (such as a min-height) to
 * the content area.
 *
 * @param {*} composableElementsConfig
 * @param {*} filteredElements
 * @param {*} groupComponent
 * @param {*} contentContainerComponent
 * @param {*} isRoot
 */
const createSections = (
  composableElementsConfig,
  filteredElements,
  groupComponent,
  contentContainerComponent,
  isRoot
) => {
  const scrollIntoViewIndex = filteredElements.findIndex(
    // eslint-disable-next-line no-underscore-dangle
    ({ __typename }) => __typename === 'ScrollIntoView'
  );

  if (scrollIntoViewIndex === -1) {
    return convertToReactElements(
      composableElementsConfig,
      filteredElements,
      groupComponent,
      isRoot
    );
  }

  // Get all elements before 'ScrollIntoView'.
  const headerElements = filteredElements.slice(0, scrollIntoViewIndex);

  // Get 'ScrollIntoView' element and all elements after 'ScrollIntoView'.
  const contentElements = filteredElements.slice(scrollIntoViewIndex);

  const reactHeaderElements = convertToReactElements(
    composableElementsConfig,
    headerElements,
    groupComponent,
    isRoot
  );

  const reactContentElements = convertToReactElements(
    composableElementsConfig,
    contentElements,
    groupComponent,
    isRoot
  );

  // Wrap the content components into a contentContainerComponent.
  const content = React.createElement(
    contentContainerComponent,
    {
      key: 'scrollToContent',
    },
    reactContentElements
  );

  return [...reactHeaderElements, content];
};

/**
 * Creates an array of React elements based on the JSON file of
 * a composable page. All placeholders are replaced and passed
 * down as props.
 */
const createReactElements = (
  json,
  {
    data,
    getFeatureName,
    groupComponent,
    contentContainerComponent,
    resizeImageSource,
    composableElementsConfig,
    htmlHeadContainer,
  },
  { featureFlags },
  isRoot
) => {
  // Replace all placeholder values with values from data
  // in the result from the GraphQL query.
  const { meta, elements = [] } = replacePlaceholderValues(data)(json);

  // Make sure that all nested arrays are moved to the root.
  const flatElements = flattenElements(elements);

  // Remove all disabled features.
  const filteredElements = featureFlags
    ? flatElements.filter(isElementFeatureEnabled(featureFlags, getFeatureName))
    : flatElements;

  const reactElements = createSections(
    composableElementsConfig,
    filteredElements,
    groupComponent,
    contentContainerComponent,
    isRoot
  );

  if (meta) {
    const head = (
      <MetaData
        containerComponent={htmlHeadContainer}
        key="MetaData"
        {...meta}
        image={resizeImageSource(meta.image)}
      />
    );

    return [head, ...reactElements];
  }

  return reactElements;
};

export default createReactElements;
